import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import IconButton from '@mui/material/IconButton';
import { green, red, amber, blue } from '@mui/material/colors';
import { ApiInstance } from '../../../api/api';
import {
  BulkDhcpReservationCreateRequestType,
  BulkDhcpReservationDialogState,
  DhcpReservationEntity,
} from '../../../types/types';
import { DeviceARPDataType } from '../../../types/buildingTypes';
import dialogStyles from '../common/dialogStyles';
import DialogContentLoading from '../common/DialogContentLoading';
import DialogContentError from '../common/DialogContentError';
import BulkDhcpReservationForm from './forms/BulkDhcpReservationForm';
import BulkDhcpReservationSuccessResult from './forms/BulkDhcpReservationSuccessResult';
import { parseApiError } from '../../../utils/apiErrorHandler';
import { formatMacAddress } from '../../../utils/macFormatter';
import useBulkDhcpReservationForm from '../../../hooks/useBulkDhcpReservationForm';
import useApplyFilterParam from '../../../hooks/useApplyFilterParam';

interface BulkDhcpReservationActionProps {
  open: boolean;
  onClose: () => void;
  ipAddress: string | undefined;
  deviceType: string | null;
  region: string | null;
  buildingCode: string | null;
  unit: string;
  unitName: string | null;
  selectedRows: DeviceARPDataType[];
}

const DEFAULT_LOADING_TEXT = 'Please wait while we process your request...';
const DEFAULT_ERROR_TEXT = 'Something went wrong. Please try again later.';

const titleMap: Record<BulkDhcpReservationDialogState, string> = {
  default: 'New DHCP Reservations',
  loading: 'New DHCP Reservations',
  success: 'New DHCP Reservations',
  error: 'New DHCP Reservations',
};

const backgroundColorMap: Record<BulkDhcpReservationDialogState, string> = {
  default: amber[800],
  loading: blue[600],
  success: green[500],
  error: red[500],
};

const BulkDhcpReservationAction: React.FC<BulkDhcpReservationActionProps> = ({
  open,
  onClose,
  ipAddress,
  deviceType,
  region,
  buildingCode,
  unit,
  unitName,
  selectedRows,
}) => {
  const {
    entries,
    setDescription,
    markTouched,
    isFormValid,
    duplicateDescriptions,
    allowedInputCharacters,
    maxDescriptionLength,
  } = useBulkDhcpReservationForm(selectedRows);

  const [dialogState, setDialogState] = useState<BulkDhcpReservationDialogState>('default');
  const [loadingText, setLoadingText] = useState(DEFAULT_LOADING_TEXT);
  const [errorText, setErrorText] = useState(DEFAULT_ERROR_TEXT);
  const [createdReservations, setCreatedReservations] = useState<DhcpReservationEntity[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const macListParam = createdReservations.map((r) => formatMacAddress(r.mac)).join(',');
  const applyFilterAfterBulkCreate = useApplyFilterParam('mac_address', macListParam);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setDialogState('default');
        setLoadingText(DEFAULT_LOADING_TEXT);
        setErrorText(DEFAULT_ERROR_TEXT);
        setCreatedReservations([]);
        setSubmitAttempted(false);
      }, 100);
    }
  }, [open]);

  const handleConfirm = async () => {
    setSubmitAttempted(true);

    if (!isFormValid) {
      return;
    }

    setDialogState('loading');
    setLoadingText(DEFAULT_LOADING_TEXT);

    try {
      const payload: BulkDhcpReservationCreateRequestType = {
        device_ip: ipAddress,
        device_vendor: deviceType,
        region: region,
        building_code: buildingCode,
        unit: unit,
        unit_name: unitName,
        reservation_data: entries.map(({ row, description }) => ({
          interface: row.interface,
          dhcp_reservation_ip: row.ip_address,
          dhcp_reservation_mac: formatMacAddress(row.mac_address),
          dhcp_reservation_description: row.dhcp_reservation_description,
          new_dhcp_reservation_description: description.toLowerCase(),
        })),
      };

      const response = await ApiInstance.createBulkDhcpReservation(payload);

      setCreatedReservations(response.data);
      setDialogState('success');
    } catch (error: any) {
      setErrorText(parseApiError(error));
      setDialogState('error');
    }
  };

  const handleClose = () => {
    if (dialogState === 'success') {
      applyFilterAfterBulkCreate();
    } else {
      onClose();
    }
  };

  const getDialogTitle = () => titleMap[dialogState];
  const getTitleBackgroundColor = () => backgroundColorMap[dialogState];

  const getDialogContent = () => {
    switch (dialogState) {
      case 'loading':
        return <DialogContentLoading loadingText={loadingText} />;
      case 'success':
        return <BulkDhcpReservationSuccessResult reservations={createdReservations} />;
      case 'error':
        return <DialogContentError errorText={errorText} />;
      default:
        return (
          <BulkDhcpReservationForm
            entries={entries}
            submitAttempted={submitAttempted}
            duplicateDescriptions={duplicateDescriptions}
            allowedInputCharacters={allowedInputCharacters}
            maxDescriptionLength={maxDescriptionLength}
            onDescriptionChange={setDescription}
            onBlur={markTouched}
          />
        );
    }
  };

  const showCloseIcon = dialogState !== 'loading';
  const showDialogActions = dialogState === 'default';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={dialogStyles.dialog}
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: 640,
          width: '100%',
        },
      }}
      aria-labelledby="bulk-dhcp-reservation-dialog-title"
      aria-describedby="bulk-dhcp-reservation-dialog-content"
    >
      <DialogTitle
        id="bulk-dhcp-reservation-dialog-title"
        sx={{
          ...dialogStyles.dialogTitle,
          backgroundColor: getTitleBackgroundColor(),
        }}
      >
        {getDialogTitle()}
        {showCloseIcon && (
          <IconButton aria-label="close" onClick={handleClose} sx={dialogStyles.dialogCloseButton}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent id="bulk-dhcp-reservation-dialog-content" sx={dialogStyles.dialogContent}>
        {getDialogContent()}
      </DialogContent>
      {showDialogActions && (
        <DialogActions sx={dialogStyles.dialogActions}>
          <Button
            onClick={handleClose}
            color="secondary"
            variant="contained"
            sx={dialogStyles.cancelButtonStyles}
          >
            <CloseIcon sx={dialogStyles.buttonIconSpacing} />
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            sx={dialogStyles.confirmButtonStyles}
          >
            <CheckIcon sx={dialogStyles.buttonIconSpacing} />
            Confirm
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BulkDhcpReservationAction;
